import React, { useEffect, useState, useRef } from "react";
import { Car, X as XIcon, Info } from "lucide-react";
import { api } from "../lib/api.js";
import { Link } from "react-router-dom";

/**
 * Vehicle Registration lookup field for SOM creation/edit flows.
 *
 * Props:
 *   value            — controlled search string (optional)
 *   onSelect(vr)     — called when user picks a VR from dropdown
 *   onClear()        — called when user clears the selection
 *   selectedVr       — the currently linked VR object (if any)
 *   compact          — smaller styling when embedded inline in SOM detail
 */
export default function VrLookupField({ onSelect, onClear, selectedVr, compact = false }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (selectedVr) return; // don't search when already selected
    if (query.length < 3) {
      setResults([]);
      setNotFound(false);
      return;
    }
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/vehicle-registrations/lookup?q=${encodeURIComponent(query)}`);
        setResults(data || []);
        setShow(true);
        setNotFound((data || []).length === 0);
      } catch {
        setResults([]);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [query, selectedVr]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSelect = (vr) => {
    onSelect?.(vr);
    setQuery("");
    setShow(false);
    setResults([]);
  };

  const inputSize = compact ? "py-1 text-xs" : "py-1.5 text-sm";

  if (selectedVr) {
    return (
      <div className="bg-[#E8F5E9] border border-[#28A745]/40 rounded-sm px-3 py-2 text-xs text-[#0F5132] flex items-center gap-2" data-testid="vr-lookup-selected">
        <Car className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Dados preenchidos a partir do <span className="font-mono">{selectedVr.vr_number}</span>
          {" — "}
          {selectedVr.customer_name}
          {" — "}
          <span className="font-mono">{selectedVr.registration_plate}</span>
        </span>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto p-0.5 hover:bg-[#28A745]/20 rounded"
          data-testid="vr-lookup-clear"
          aria-label="Limpar selecção VR"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Pesquisar viatura por matrícula</span>
        <div className="relative mt-0.5">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            onFocus={() => query.length >= 3 && setShow(true)}
            placeholder="Ex.: ABA-1234-M"
            className={`w-full pl-8 pr-3 ${inputSize} border border-gray-300 rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30`}
            data-testid="vr-lookup-input"
          />
          <Car className="w-4 h-4 text-[#6C757D] absolute left-2 top-1/2 -translate-y-1/2" />
        </div>
      </label>

      {show && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-72 overflow-y-auto" data-testid="vr-lookup-dropdown">
          {results.map((vr) => (
            <button
              type="button"
              key={vr.id}
              onClick={() => handleSelect(vr)}
              className="w-full text-left px-3 py-2 hover:bg-[#F4F6F8] border-b border-gray-100 last:border-0"
              data-testid={`vr-lookup-option-${vr.id}`}
            >
              <div className="flex items-center gap-3 text-sm">
                <span className="font-mono text-[#1F3864] font-semibold">{vr.registration_plate}</span>
                <span className="text-[#1A1A2E]">{vr.brand} {vr.model}</span>
                <span className="text-[#6C757D] text-xs ml-auto">{vr.customer_name}</span>
              </div>
              <div className="text-[10px] text-[#6C757D] font-mono mt-0.5">
                {vr.vr_number} · VIN {vr.vin}
              </div>
            </button>
          ))}
        </div>
      )}

      {loading && query.length >= 3 && (
        <div className="text-[10px] text-[#6C757D] mt-1">A pesquisar…</div>
      )}

      {notFound && query.length >= 3 && !loading && (
        <div className="mt-2 bg-[#FFF7E6] border border-[#FFC107]/40 text-[#856404] rounded-sm px-3 py-2 text-xs flex items-center gap-2" data-testid="vr-lookup-not-found">
          <Info className="w-4 h-4 shrink-0" />
          <span>
            Matrícula não encontrada no registo de viaturas. Preenche os dados manualmente ou{" "}
            <Link to="/vehicle-registrations" className="underline font-medium" onClick={() => setShow(false)}>
              regista a viatura primeiro
            </Link>.
          </span>
        </div>
      )}
    </div>
  );
}
